import React, { Fragment, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
// import { navigate } from 'gatsby'
import {
  Accordion as ReachAccordion,
  AccordionItem as ReachAccordionItem,
  AccordionButton as ReachAccordionButton,
  AccordionPanel as ReachAccordionPanel,
} from '@reach/accordion'
import '@reach/accordion/styles.css'
import { useId } from '@reach/auto-id'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { animated, useTransition } from 'react-spring'
import classNames from 'classnames'

import styles from './styles.module.scss'

const childrenPropType = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.node),
  PropTypes.node,
])

// Most of the changes made here are just changing the Reach UI API to use
// is/has/can etc naming convention for boolean props for better legibility

// We also extend it to use animation & styles

const AccordionContext = React.createContext()
const useAccordionContext = () => React.useContext(AccordionContext)

function getElementHeight(ref) {
  return ref.current ? ref.current.getBoundingClientRect().height : 0
}

// -------------------------------------------
// #region Accordion
// Wrapper Provider for actual Accordion
// -------------------------------------------
export const Accordion = React.forwardRef(
  ({ hasHistory, hasStickyHeaders, onOpened, children, ...props }, ref) => {
    const [localOpenedIndexes, setLocalOpenedIndexes] = useState([])

    const context = {
      hasHistory,
      hasStickyHeaders,
      localOpenedIndexes,
      setLocalOpenedIndexes,
      onOpened,
    }
    return (
      <AccordionContext.Provider value={context}>
        <AccordionContents ref={ref} {...props}>
          {children}
        </AccordionContents>
      </AccordionContext.Provider>
    )
  }
)

Accordion.displayName = 'Accordion'

Accordion.propTypes = {
  hasHistory: PropTypes.bool,
  hasStickyHeaders: PropTypes.bool,
  children: childrenPropType.isRequired,
}

Accordion.defaultProps = {
  hasHistory: false,
  hasStickyHeaders: true,
}
//
// #endregion Accordion
// -------------------------------------------

// -------------------------------------------
// #region AccordionContents
// -------------------------------------------
const AccordionContents = ({
  canCollapseAll,
  canOpenMultiple,
  defaultIndex,
  index,
  onChange,
  children,
  isReadOnly,
  className,
  hasHistory,
  items,
  ...props
}) => {
  const { localOpenedIndexes, setLocalOpenedIndexes } = useAccordionContext()
  // Track opened indexes locally because Reach Accordion's don't trigger re-renders
  // on change, so we can't pull state from the dataset
  // (if you know how to trigger a re-render, then we can refactor)
  useEffect(() => {
    if (defaultIndex !== undefined) {
      setLocalOpenedIndexes(
        Number.isInteger(defaultIndex) ? [defaultIndex] : [...defaultIndex]
      )
    }
  }, [defaultIndex, setLocalOpenedIndexes])

  useEffect(() => {
    if (index !== undefined) {
      setLocalOpenedIndexes(Number.isInteger(index) ? [index] : [...index])
    }
  }, [index, setLocalOpenedIndexes])

  const handleOnChange = (toggledIndex) => {
    let newLocalOpenedIndexes
    if (!index) {
      if (!canOpenMultiple) {
        newLocalOpenedIndexes = [toggledIndex]
      }
      if (canOpenMultiple) {
        newLocalOpenedIndexes = [...localOpenedIndexes, toggledIndex]
      }
      if (canCollapseAll && localOpenedIndexes.includes(toggledIndex)) {
        newLocalOpenedIndexes = localOpenedIndexes.filter(
          (openIndex) => openIndex !== toggledIndex
        )
      }
    } else {
      newLocalOpenedIndexes = [index]
    }
    setLocalOpenedIndexes(newLocalOpenedIndexes)
    // Pass back opened indexes so you don't
    // have to use a controlled accordion to know
    // what indexes are open
    if (onChange) onChange(newLocalOpenedIndexes)
  }

  const autoId = useId()

  if (items) {
    return (
      <ReachAccordion
        collapsible={canCollapseAll}
        multiple={canOpenMultiple}
        defaultIndex={defaultIndex}
        index={index}
        onChange={handleOnChange}
        readOnly={isReadOnly}
        className={classNames(styles.accordion, className)}
        {...props}
      >
        {items.map((item, itemIndex) => (
          <Fragment key={autoId}>
            <AccordionItem index={itemIndex}>
              <AccordionButton
                index={itemIndex}
                link={item.link ? item.link : undefined}
              >
                <AccordionIcon index={itemIndex} />
                {item.header}
              </AccordionButton>
              {item.summary && (
                <AccordionSummary index={itemIndex}>
                  {item.summary}
                </AccordionSummary>
              )}
              <AccordionPanel index={itemIndex}>{item.body}</AccordionPanel>
            </AccordionItem>
          </Fragment>
        ))}
      </ReachAccordion>
    )
  }

  return (
    <ReachAccordion
      collapsible={canCollapseAll}
      multiple={canOpenMultiple}
      defaultIndex={defaultIndex}
      index={index}
      onChange={handleOnChange}
      readOnly={isReadOnly}
      className={classNames(styles.accordion, className)}
      {...props}
    >
      {React.Children.map(children, (child, childIndex) => {
        if (
          React.isValidElement(child) &&
          child?.type?.displayName?.startsWith('Accordion')
        ) {
          return React.cloneElement(child, {
            index: childIndex,
          })
        }
        return child
      })}
    </ReachAccordion>
  )
}

AccordionContents.displayName = 'AccordionContents'

AccordionContents.propTypes = {
  canCollapseAll: PropTypes.bool,
  canOpenMultiple: PropTypes.bool,
  children: childrenPropType.isRequired,
  className: PropTypes.string,
  defaultIndex: PropTypes.number,
  hasHistory: PropTypes.bool,
  index: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
  isReadOnly: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      summary: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      body: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      link: PropTypes.string,
    })
  ),
  link: PropTypes.string,
  onChange: PropTypes.func,
}

AccordionContents.defaultProps = {
  defaultIndex: undefined,
  index: undefined,
  link: undefined,
  canCollapseAll: undefined,
  canOpenMultiple: undefined,
  className: undefined,
  hasHistory: undefined,
  isReadOnly: undefined,
  items: undefined,
  onChange: undefined,
}

//
// #endregion AccordionContents
// -------------------------------------------

// -------------------------------------------
// #region AccordionItem
// -------------------------------------------
export const AccordionItem = ({
  isDisabled,
  className,
  children,
  index,
  ...props
}) => (
  <ReachAccordionItem
    className={classNames(styles.item, className)}
    disabled={isDisabled}
    {...props}
  >
    {React.Children.map(children, (child) => {
      if (
        React.isValidElement(child) &&
        child?.type?.displayName?.startsWith('Accordion')
      ) {
        return React.cloneElement(child, {
          index,
        })
      }
      return child
    })}
  </ReachAccordionItem>
)

AccordionItem.displayName = 'AccordionItem'

AccordionItem.propTypes = {
  className: PropTypes.string,
  index: PropTypes.number,
  isDisabled: PropTypes.bool,
  children: childrenPropType.isRequired,
}

AccordionItem.defaultProps = {
  className: undefined,
  isDisabled: undefined,
  index: undefined,
}
//
// #endregion AccordionItem
// -------------------------------------------

// -------------------------------------------
// #region AccordionSummary
// -------------------------------------------
export const AccordionSummary = ({
  children,
  isHiddenWhenOpen,
  index,
  className,
  ...props
}) => {
  const { localOpenedIndexes } = useAccordionContext()
  const isOpen = localOpenedIndexes.includes(index)
  const isVisibleOnMount = useRef(isOpen)
  const outerRef = useRef()
  const innerRef = useRef()
  const shouldShow = (isHiddenWhenOpen && !isOpen) || !isHiddenWhenOpen

  const visibleStyle = { height: 'auto', opacity: 1, overflow: 'visible' }
  const hiddenStyle = { opacity: 0, height: 0, overflow: 'hidden' }

  const animateSummary = useTransition(shouldShow, null, {
    enter: () => async (next, cancel) => {
      if (!shouldShow) {
        cancel()
        return
      }
      const height = getElementHeight(innerRef)
      await next({ height, opacity: 1, overflow: 'hidden' })
      await next(visibleStyle)
    },
    leave: () => async (next, cancel) => {
      if (shouldShow) {
        cancel()
        return
      }
      const height = getElementHeight(outerRef)
      await next({ height, overflow: 'hidden' })
      await next(hiddenStyle)

      isVisibleOnMount.current = false
    },
    from: !isVisibleOnMount.current ? visibleStyle : hiddenStyle,
    unique: true,
    config: {
      mass: 1,
      tension: 300,
      friction: 40,
    },
  })

  return animateSummary.map(({ item, key, props: animatedStyles }) => {
    if (item) {
      return (
        <animated.div
          ref={outerRef}
          key={key}
          className={classNames(className)}
          style={animatedStyles}
          {...props}
        >
          <div ref={innerRef}>{children}</div>
        </animated.div>
      )
    }
    return false
  })
}

AccordionSummary.displayName = 'AccordionSummary'

AccordionSummary.propTypes = {
  children: childrenPropType.isRequired,
  className: PropTypes.string,
  index: PropTypes.number,
  isHiddenWhenOpen: PropTypes.bool,
}

AccordionSummary.defaultProps = {
  index: undefined,
  className: null,
  isHiddenWhenOpen: true,
}
//
// #endregion AccordionSummary
// -------------------------------------------

// -------------------------------------------
// #region AccordionButton
// -------------------------------------------

export const AccordionButton = React.forwardRef(
  ({ children, link, className, onClick, ...props }, ref) => {
    const { hasStickyHeaders } = useAccordionContext()

    const handleClick = () => {
      if (onClick) onClick()
      // if (link) navigate(link)
    }

    return (
      <ReachAccordionButton
        className={classNames(styles.button, className, {
          [styles.hasStickyHeader]: hasStickyHeaders,
        })}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        {children}
      </ReachAccordionButton>
    )
  }
)

AccordionButton.displayName = 'AccordionButton'

AccordionButton.propTypes = {
  children: childrenPropType.isRequired,
  className: PropTypes.string,
  link: PropTypes.string,
  onClick: PropTypes.func,
}

AccordionButton.defaultProps = {
  className: undefined,
  link: undefined,
  onClick: undefined,
}
//
// #endregion AccordionButton
// -------------------------------------------

// -------------------------------------------
// #region AccordionPanel
// -------------------------------------------
export const AccordionPanel = ({ children, className, index, ...props }) => {
  const AnimatedReachAccordionPanel = animated(ReachAccordionPanel)
  const { localOpenedIndexes, onOpened } = useAccordionContext()
  const outerRef = useRef()
  const innerRef = useRef()
  const isOpen = localOpenedIndexes.includes(+index)
  const isVisibleOnMount = useRef(isOpen)

  const visibleStyle = { height: 'auto', opacity: 1, overflow: 'visible' }
  const hiddenStyle = { opacity: 0, height: 0, overflow: 'hidden' }

  const animatePanel = useTransition(isOpen, null, {
    enter: () => async (next, cancel) => {
      // FIXME: this cancel stuff is bogus which is
      // why I added the if(!isOpen) stuff
      if (!isOpen) {
        cancel()
        return
      }
      const height = getElementHeight(innerRef)
      await next({
        height: getElementHeight(innerRef),
        opacity: 1,
        overflow: 'hidden',
      })
      await next(visibleStyle)
    },
    leave: () => async (next, cancel) => {
      if (isOpen) {
        cancel()
        return
      }
      const height = getElementHeight(outerRef)
      await next({ height: getElementHeight(outerRef), overflow: 'hidden' })
      await next(hiddenStyle)

      isVisibleOnMount.current = false
    },
    from: isVisibleOnMount.current ? visibleStyle : hiddenStyle,
    unique: true,
    onRest: () => onOpened && onOpened(),
    config: {
      mass: 1,
      tension: 300,
      friction: 40,
    },
  })

  return animatePanel.map(({ item, key, props: animatedStyles }) => {
    if (item) {
      return (
        <AnimatedReachAccordionPanel
          ref={outerRef}
          key={key}
          className={classNames(styles.panel, className)}
          style={animatedStyles}
          {...props}
        >
          <div ref={innerRef}>{children}</div>
        </AnimatedReachAccordionPanel>
      )
    }
    return false
    // return <Fragment key={key}></React.Fragment>
  })
}

AccordionPanel.displayName = 'AccordionPanel'

AccordionPanel.propTypes = {
  children: childrenPropType.isRequired,
  className: PropTypes.string,
  index: PropTypes.number,
}

AccordionPanel.defaultProps = {
  className: undefined,
  index: undefined,
}

//
// #endregion AccordionPanel
// -------------------------------------------

// -------------------------------------------
// #region AccordionIcon
// -------------------------------------------

// TODO: Add ability to pass custom, non-FontAwesome icons
export const AccordionIcon = ({ className, icon, ...props }) => (
  <FontAwesomeIcon
    className={classNames(styles.icon, className)}
    icon={icon}
    {...props}
  />
)

AccordionIcon.displayName = 'AccordionIcon'

AccordionIcon.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.string,
}

AccordionIcon.defaultProps = {
  className: undefined,
  icon: 'chevron-down',
}

//
// #endregion AccordionIcon
// -------------------------------------------
